/** Hesap ve güvenlik uçları (M6 WP 6.2; docs/spec/castinflow/settings.md). Bütün çağrılar oturum sahibinin kendi hesabıdır. */
import type {
  LoginResponse,
  NotificationPreferencesResponse,
  PasskeyItem,
  Profile,
  ProfileUpdateInput,
  SecurityOverview,
  SessionListItem,
} from '@castinflow/shared';
import { apiFetch } from '../../lib/api';
import { apiMutate } from '../../lib/session';
import type { Scope } from '../../lib/scope';

export const accountKeys = {
  security: (s: Scope) => ['t', s.tenantId, 'u', s.userId, 'account', 'security'] as const,
  sessions: (s: Scope) => ['t', s.tenantId, 'u', s.userId, 'account', 'sessions'] as const,
  notifications: (s: Scope) =>
    ['t', s.tenantId, 'u', s.userId, 'account', 'notification-preferences'] as const,
};

export const fetchSecurity = () => apiFetch<SecurityOverview>('/auth/security');
export const updateProfile = (input: ProfileUpdateInput) =>
  apiMutate<Profile>('/auth/profile', 'PATCH', input);
export const changePassword = (currentPassword: string, newPassword: string) =>
  apiMutate<{ revokedSessions: number }>('/auth/change-password', 'POST', {
    currentPassword,
    newPassword,
  });

export const fetchSessions = () => apiFetch<{ sessions: SessionListItem[] }>('/auth/sessions');
export const revokeSession = (id: string) => apiMutate<unknown>(`/auth/sessions/${id}`, 'DELETE');
export const revokeOtherSessions = () =>
  apiMutate<{ revokedSessions: number }>('/auth/sessions/revoke-others', 'POST');

export const totpSetup = () =>
  apiMutate<{ secret: string; otpauthUrl: string }>('/auth/totp/setup', 'POST');
export const totpConfirm = (code: string) =>
  apiMutate<{ enabled: true; recoveryCodes: string[] }>('/auth/totp/confirm', 'POST', { code });
export const totpDisable = (password: string, code: string) =>
  apiMutate<unknown>('/auth/totp/disable', 'POST', { password, code });
export const regenerateRecoveryCodes = (password: string) =>
  apiMutate<{ recoveryCodes: string[] }>('/auth/totp/recovery-codes', 'POST', { password });

export const passkeyRegisterOptions = (password: string) =>
  apiMutate<{ options: Record<string, unknown> }>('/auth/passkeys/register/options', 'POST', {
    password,
  });
export const passkeyRegisterVerify = (name: string, response: unknown) =>
  apiMutate<PasskeyItem>('/auth/passkeys/register/verify', 'POST', { name, response });
export const passkeyRemove = (id: string, password: string) =>
  apiMutate<unknown>(`/auth/passkeys/${id}/remove`, 'POST', { password });

/** Passkey ile giriş (oturumsuz): CSRF belirteci yoktur; Origin sunucuda denetlenir. */
const postJson = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
export const passkeyLoginOptions = (tenantSlug: string) =>
  postJson<{ challengeId: string; options: Record<string, unknown> }>(
    '/auth/passkey/login/options',
    { tenantSlug },
  );
export const passkeyLoginVerify = (challengeId: string, response: unknown) =>
  postJson<LoginResponse>('/auth/passkey/login/verify', { challengeId, response });

export const fetchNotificationPreferences = () =>
  apiFetch<NotificationPreferencesResponse>('/me/notification-preferences');
export const saveNotificationPreference = (key: string, inApp: boolean, email?: boolean) =>
  apiMutate<NotificationPreferencesResponse>('/me/notification-preferences', 'PUT', {
    events: [email === undefined ? { key, inApp } : { key, inApp, email }],
  });
/** v3.1 C4: günlük özet e-postası */
export const saveNotificationDigest = (enabled: boolean) =>
  apiMutate<NotificationPreferencesResponse>('/me/notification-preferences', 'PUT', {
    events: [],
    digest: { enabled },
  });
